import { ActionIcon, Indicator, Popover } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconMessages } from '@tabler/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { chatService } from '../../data/chat';
import { useChat } from '../../hooks/use-chat';
import { ConversationParticipant, Message } from '../../models/chat';
import { RootState } from '../../providers/store';
import { DaySeparator } from './components/DaySeparator';
import { ImagePreviewModal } from './components/ImagePreviewModal';
import { LeftPane } from './components/LeftPane';
import { MessageBubble } from './components/MessageBubble';
import { RightPane } from './components/RightPane';
import { getActiveChatTitle, getDateKey, getDayLabel } from './utils';

type PanelView = 'list' | 'users' | 'new-group';

export function ChatWidget() {
  const [opened, setOpened] = useState(false);
  const [view, setView] = useState<PanelView>('list');
  const [activeConversationId, setActiveConversationId] = useState<
    number | undefined
  >();
  const [selectedUserForNewChat, setSelectedUserForNewChat] = useState<{
    id: number;
    name: string;
    email: string;
  } | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const { user } = useSelector((state: RootState) => state.auth);

  const {
    messages,
    conversations,
    users,
    nextCursor,
    loadingOlder,
    loadingMessages,
    loadingConversations,
    sendMessage,
    startConversation,
    createGroupConversation,
    fetchMessages,
    fetchOlderMessages,
    fetchConversations,
    fetchUsers,
  } = useChat(activeConversationId, {
    isActive: opened && !!activeConversationId,
  });

  const viewport = useRef<HTMLDivElement>(null);
  const prevFirstMessageId = useRef<number | string | null>(null);
  // Throttle ref for scroll handler — prevents firing more than once per 100ms.
  const lastScrollCallRef = useRef(0);

  // --- Effects ---

  useEffect(() => {
    fetchConversations(true);
  }, [fetchConversations]);

  useEffect(() => {
    prevFirstMessageId.current = null;
  }, [activeConversationId, opened]);

  useEffect(() => {
    if (!opened || loadingMessages || messages.length === 0) return;
    const firstId = messages[0].id;
    const isHistoryPrepend =
      prevFirstMessageId.current !== null &&
      firstId !== prevFirstMessageId.current;
    if (!isHistoryPrepend) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (viewport.current)
            viewport.current.scrollTop = viewport.current.scrollHeight;
        }),
      );
    }
    prevFirstMessageId.current = firstId;
  }, [messages, loadingMessages, opened]);

  useEffect(() => {
    if (opened) fetchConversations();
  }, [fetchConversations, opened]);

  useEffect(() => {
    if (!opened) return;
    if (view === 'users') fetchUsers();
    if (activeConversationId) fetchMessages(activeConversationId);
  }, [opened, view, activeConversationId, fetchUsers, fetchMessages]);

  // --- Callbacks ---

  const handleSelectConversation = useCallback((id: number) => {
    setActiveConversationId(id);
    setSelectedUserForNewChat(null);
  }, []);

  const handleSelectUser = useCallback(
    async (u: any) => {
      const existing = conversations.find(
        (c) =>
          c.type === 'private' &&
          c.participants.some(
            (p: ConversationParticipant) => p.user.id === u.id,
          ),
      );
      if (existing) {
        handleSelectConversation(existing.id);
      } else {
        setSelectedUserForNewChat(u);
        setActiveConversationId(undefined);
      }
    },
    [conversations, handleSelectConversation],
  );

  const handleSend = useCallback(
    async (content: string) => {
      if (!content.trim()) return;
      if (activeConversationId) {
        await sendMessage(content);
      } else if (selectedUserForNewChat && user) {
        const conv = await startConversation(selectedUserForNewChat.id);
        setActiveConversationId(conv.id);
        await chatService.sendMessage(conv.id, content, user.id);
        fetchConversations();
      }
    },
    [
      activeConversationId,
      selectedUserForNewChat,
      user,
      sendMessage,
      startConversation,
      fetchConversations,
    ],
  );

  const handleSendAttachment = useCallback(
    async (file: globalThis.File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploaded } = await import('../../providers/base-api').then(
        ({ api }) =>
          api.post('/files', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }),
      );
      const attachment = {
        url: uploaded.url as string,
        name: uploaded.name as string,
        type: file.type,
      };
      if (activeConversationId && user) {
        await chatService.sendMessage(
          activeConversationId,
          '',
          user.id,
          attachment,
        );
      } else if (selectedUserForNewChat && user) {
        const conv = await startConversation(selectedUserForNewChat.id);
        setActiveConversationId(conv.id);
        await chatService.sendMessage(conv.id, '', user.id, attachment);
        fetchConversations();
      }
    },
    [
      activeConversationId,
      selectedUserForNewChat,
      user,
      startConversation,
      fetchConversations,
    ],
  );

  /**
   * Throttled scroll handler — fires at most once per 100ms.
   * The Mantine ScrollArea fires this on every scroll pixel, so without
   * throttling it triggers fetchOlderMessages unnecessarily many times.
   */
  const handleScrollPositionChange = useCallback(
    ({ y }: { x: number; y: number }) => {
      if (y < 40 && nextCursor && !loadingOlder) {
        const now = Date.now();
        if (now - lastScrollCallRef.current < 100) return;
        lastScrollCallRef.current = now;

        const el = viewport.current;
        const prevHeight = el?.scrollHeight ?? 0;
        fetchOlderMessages().then(() => {
          requestAnimationFrame(() => {
            if (el) el.scrollTo({ top: el.scrollHeight - prevHeight });
          });
        });
      }
    },
    [nextCursor, loadingOlder, fetchOlderMessages],
  );

  // Stable callbacks passed to memoized child components.
  // Without these, every inline `() => ...` would create a new function on
  // each render, defeating React.memo on LeftPane and RightPane.
  const handleBack = useCallback(() => {
    setActiveConversationId(undefined);
    setSelectedUserForNewChat(null);
  }, []);
  const handleClosePreview = useCallback(() => setPreviewImage(null), []);

  // --- Derived values ---

  const isChatActive = !!activeConversationId || !!selectedUserForNewChat;

  const filteredUsers = useMemo(
    () => users.filter((u) => u.id !== user?.id),
    [users, user?.id],
  );

  const activeChatTitle = useMemo(
    () =>
      getActiveChatTitle(
        conversations,
        activeConversationId,
        user?.id,
        selectedUserForNewChat,
      ),
    [conversations, activeConversationId, user?.id, selectedUserForNewChat],
  );

  const renderedMessages = useMemo(() => {
    const elements: React.ReactNode[] = [];
    let lastDateKey = '';

    messages.forEach((msg: Message) => {
      const dateKey = getDateKey(msg.createdAt);
      if (dateKey !== lastDateKey) {
        elements.push(
          <DaySeparator
            key={`day-${dateKey}`}
            label={getDayLabel(msg.createdAt)}
          />,
        );
        lastDateKey = dateKey;
      }
      const msgSenderId =
        (typeof msg.sender === 'object' ? msg.sender?.id : msg.sender) ||
        (msg as any).sender_id ||
        (msg as any).senderId;
      elements.push(
        <MessageBubble
          key={msg.id}
          msg={msg}
          isMe={String(msgSenderId) === String(user?.id)}
          onPreviewImage={setPreviewImage}
        />,
      );
    });
    return elements;
  }, [messages, user?.id]);

  const totalUnread = useMemo(
    () => conversations.reduce((acc, c) => acc + (c.unreadCount ?? 0), 0),
    [conversations],
  );

  // --- Render ---

  return (
    <>
      <Popover
        width={isMobile ? 'calc(100vw - 32px)' : 420}
        position="bottom-end"
        withArrow
        shadow="md"
        opened={opened}
        onChange={setOpened}
      >
        <Popover.Target>
          <Indicator
            label={totalUnread}
            size={16}
            overflowCount={99}
            disabled={totalUnread === 0}
            color="red"
            withBorder
          >
            <ActionIcon
              color="ltpBlue.9"
              radius={15}
              variant="light"
              onClick={() => setOpened((o) => !o)}
              size={30}
            >
              <IconMessages size={20} />
            </ActionIcon>
          </Indicator>
        </Popover.Target>
        <Popover.Dropdown p={0} sx={{ overflow: 'hidden' }}>
          <div style={{ width: '100%', height: 500, display: 'flex' }}>
            {!isChatActive && (
              <LeftPane
                view={view}
                onViewChange={setView}
                conversations={conversations}
                loadingConversations={loadingConversations}
                users={filteredUsers}
                userId={user?.id}
                onSelectConversation={handleSelectConversation}
                onSelectUser={handleSelectUser}
                createGroupConversation={createGroupConversation}
                fetchConversations={fetchConversations}
                onGroupCreated={setActiveConversationId}
                isMobile={true}
              />
            )}
            {isChatActive && (
              <RightPane
                activeChatTitle={activeChatTitle}
                isChatActive={isChatActive}
                isMobile={true}
                isDragging={false}
                loadingMessages={loadingMessages}
                loadingOlder={loadingOlder}
                messages={messages}
                renderedMessages={renderedMessages}
                selectedUserName={selectedUserForNewChat?.name}
                viewport={viewport}
                onScrollPositionChange={handleScrollPositionChange}
                onSend={handleSend}
                onSendAttachment={handleSendAttachment}
                onClose={() => setOpened(false)}
                onBack={handleBack}
                onMouseDown={() => {}}
              />
            )}
          </div>
        </Popover.Dropdown>
      </Popover>

      <ImagePreviewModal url={previewImage} onClose={handleClosePreview} />
    </>
  );
}
